import React from 'react';
import { Bell, ChevronLeft, ChevronRight } from "lucide-react";
import NotificationItem from './NotificationItem';
import { Notification } from '@/types/notification';
import {
  Table,
  TableBody,
  TableCaption,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface NotificationsListProps {
  notifications: Notification[];
  isLoading: boolean;
  onMarkAsRead: (id: string) => void;
  onMarkAsUnread?: (id: string) => void;
  onRemove: (id: string) => void;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export const NotificationsList: React.FC<NotificationsListProps> = ({
  notifications,
  isLoading,
  onMarkAsRead,
  onMarkAsUnread,
  onRemove,
  currentPage,
  itemsPerPage,
  onPageChange,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-6">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-white"></div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <Card className="p-6 flex flex-col items-center justify-center">
        <Bell className="h-10 w-10 text-muted-foreground mb-3" />
        <h2 className="text-lg font-semibold mb-1">No notifications</h2>
        <p className="text-sm text-muted-foreground">You don't have any notifications yet.</p>
      </Card>
    );
  }

  const totalPages = Math.ceil(notifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, notifications.length);
  const currentNotifications = notifications.slice(startIndex, endIndex);

  return (
    <div className="space-y-4">
      <Table className="border-collapse">
        <TableCaption>
          Showing {startIndex + 1}-{endIndex} of {notifications.length} notifications
        </TableCaption>
        <TableHeader>
          <TableRow className="border-b">
            <TableHead className="w-[80px] text-center">Status</TableHead>
            <TableHead className="w-full">Message</TableHead>
            <TableHead className="hidden sm:table-cell w-[150px] text-center">Date</TableHead>
            <TableHead className="w-[70px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {currentNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              id={notification.id}
              title={notification.title}
              message={notification.message}
              type={notification.type}
              isRead={notification.isRead}
              createdAt={notification.createdAt}
              onMarkAsRead={onMarkAsRead}
              onMarkAsUnread={onMarkAsUnread}
              onRemove={onRemove}
            />
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => onPageChange(currentPage - 1)}
                aria-disabled={currentPage === 1}
                className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
              />
            </PaginationItem>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => onPageChange(page)}
                  isActive={currentPage === page}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                onClick={() => onPageChange(currentPage + 1)}
                aria-disabled={currentPage === totalPages}
                className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

export default NotificationsList; 